import { areObjectsEqual } from '@monofront/business/utils';
import { IShoppingCart } from '@monofront/types';

const hasCartChanged = (
  newShoppingCart: IShoppingCart,
  oldShoppingCart: IShoppingCart | null,
) => {
  return !areObjectsEqual(newShoppingCart, oldShoppingCart);
};

const isUpdateAddToCart = (
  newShoppingCart: IShoppingCart,
  oldShoppingCart: IShoppingCart | null,
) => {
  if (!oldShoppingCart) {
    return false;
  }

  const hasNewSkus =
    newShoppingCart.products.length > oldShoppingCart.products.length;

  if (hasNewSkus) return true;

  let hasSkuQuantityIncreased = false;

  for (const newProduct of newShoppingCart.products) {
    const oldProduct = oldShoppingCart.products.find(
      (old) => old.sku === newProduct.sku,
    );

    if (!oldProduct) continue;

    const hasItemQuantityIncreased = newProduct.quantity > oldProduct.quantity;

    if (hasItemQuantityIncreased) {
      hasSkuQuantityIncreased = true;
      break;
    }
  }

  if (hasSkuQuantityIncreased) return true;

  return false;
};

const isUpdateRemoveFromCart = (
  newShoppingCart: IShoppingCart,
  oldShoppingCart: IShoppingCart | null,
) => {
  if (!oldShoppingCart) {
    return false;
  }

  const hasLessSkus =
    newShoppingCart.products.length < oldShoppingCart.products.length;

  if (hasLessSkus) return true;

  let hasSkuQuantityDecreased = false;

  for (const newProduct of newShoppingCart.products) {
    const oldProduct = oldShoppingCart.products.find(
      (old) => old.sku === newProduct.sku,
    );

    if (!oldProduct) continue;

    const hasItemQuantityDecreased =
      newProduct.quantity < (oldProduct?.quantity ?? 0);

    if (hasItemQuantityDecreased) {
      hasSkuQuantityDecreased = true;
      break;
    }
  }

  if (hasSkuQuantityDecreased) return true;

  return false;
};

export type CartUpdateStatus = 'add-to-cart' | 'remove-from-cart' | 'stale';

export const getCartUpdateStatus = (
  newShoppingCart: IShoppingCart,
  oldShoppingCart: IShoppingCart | null,
): CartUpdateStatus => {
  if (!hasCartChanged(newShoppingCart, oldShoppingCart)) {
    return 'stale';
  }

  if (isUpdateAddToCart(newShoppingCart, oldShoppingCart)) {
    return 'add-to-cart';
  }

  if (isUpdateRemoveFromCart(newShoppingCart, oldShoppingCart)) {
    return 'remove-from-cart';
  }

  return 'stale';
};
